/** 1s, 2s, 4s … capped at 30s. No sticky terminal state (fixes spec §4.3). */
export const nextReconnectDelayMs = (attempt: number): number =>
  Math.min(1000 * 2 ** attempt, 30_000);

/** Same params as upstash (spec §4.2). A channel without an ack replays from its own first-registration time. */
export function buildStreamUrl(
  url: string,
  channels: readonly string[],
  acks: ReadonlyMap<string, string>,
  since: ReadonlyMap<string, number>,
): string {
  const params = new URLSearchParams();
  for (const channel of channels) params.append("channel", channel);
  for (const channel of channels) {
    // The Date.now() fallback is unreachable via setChannels (it fills `since` first); it keeps this total.
    const from = acks.get(channel) ?? String(since.get(channel) ?? Date.now());
    params.set(`last_ack_${channel}`, from);
  }
  return `${url}?${params.toString()}`;
}

/** What one `getToken()` call gave. A failed fetch is not a sign-out: the viewer may well be signed in. */
export type TokenOutcome =
  | { kind: "token"; token: string }
  | { kind: "signed-out" }
  | { kind: "failed" };

/**
 * The channels one attempt asks the relay for; an empty list means "make no request".
 * Signed out, only the public ones (the relay rejects a tokenless private channel, and sign-in
 * remounts the connection). A failed token fetch asks for nothing unless every channel is public:
 * opening the public ones alone would report `connected` while a signed-in viewer's private
 * channels silently wait for the next reconnect instead of the next token check.
 */
export function channelsToRequest(
  channels: readonly string[],
  token: TokenOutcome,
  isPublicChannel: (channel: string) => boolean,
): readonly string[] {
  if (token.kind === "token") return channels;
  const publicChannels = channels.filter(isPublicChannel);
  if (token.kind === "signed-out") return publicChannels;
  return publicChannels.length === channels.length ? channels : [];
}
